"use client";

import { Button } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { ReactNode, useState } from "react";

export interface PageHeaderProps {
  sportIcon?: string;
  sportLabel?: string;
  statusLabels?: Array<{ icon: string; label: string; color?: string }>;
  pretitle?: string;
  title: string;
  description?: string;
  headerImage?: string;
  headerImageFullWidth?: boolean;
  actions?: ReactNode[];
}

const DESCRIPTION_TRUNCATE_LENGTH = 200;

export function PageHeader({
  sportIcon,
  sportLabel,
  statusLabels,
  pretitle,
  title,
  description,
  headerImage,
  headerImageFullWidth = false,
  actions,
}: PageHeaderProps) {
  const t = useTranslations();
  const [isExpanded, setIsExpanded] = useState(false);

  const shouldTruncate =
    description && description.length > DESCRIPTION_TRUNCATE_LENGTH;
  const displayDescription =
    description && shouldTruncate && !isExpanded
      ? description.slice(0, DESCRIPTION_TRUNCATE_LENGTH).trimEnd() + "..."
      : description;

  return (
    <header className="relative my-6 sm:my-12">
      <div className="tournament-header text-center py-3 sm:py-8 md:py-10 px-3 relative flex flex-col items-center justify-center">
        {/* Decorative top line - hidden on mobile */}
        <div className="hidden sm:block absolute left-1/2 top-0 h-px w-[150px] md:w-[200px] -translate-x-1/2 bg-gradient-to-r from-transparent via-amber-500/50 to-transparent" />

        {/* Sport indicator */}
        {sportIcon && sportLabel && (
          <div className="mb-1 sm:mb-2 flex items-center justify-center gap-2 text-white/60">
            <Icon icon={sportIcon} className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            <span className="font-spectral text-xs sm:text-sm">{sportLabel}</span>
          </div>
        )}

        {/* Pretitle */}
        {pretitle && (
          <span className="mb-2 sm:mb-4 inline-block font-cinzel text-xs sm:text-lg font-semibold uppercase tracking-[0.08em] sm:tracking-[0.14em] text-amber-500/80">
            {pretitle}
          </span>
        )}

        {/* Status labels */}
        {statusLabels && statusLabels.length > 0 && (
          <div className="mb-4 flex flex-col items-center gap-1.5">
            {statusLabels.map((status, index) => (
              <div
                key={index}
                className={`flex items-center justify-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${
                  status.color === "amber"
                    ? "bg-amber-500/15 text-amber-400"
                    : status.color === "emerald"
                      ? "bg-emerald-500/15 text-emerald-400"
                      : status.color === "rose"
                        ? "bg-rose-500/15 text-rose-400"
                        : "bg-white/10 text-white/70"
                }`}
              >
                <Icon icon={status.icon} className="h-3.5 w-3.5" />
                <span>{status.label}</span>
              </div>
            ))}
          </div>
        )}

        {/* Title */}
        <h1 className="font-cinzel text-[clamp(1.125rem,5.5vw,1.5rem)] md:text-[2.5rem] lg:text-5xl font-bold sm:font-black leading-tight sm:leading-normal tracking-[0.02em] sm:tracking-[0.06em] [overflow-wrap:anywhere] hyphens-auto bg-gradient-to-br from-white to-amber-400 bg-clip-text text-transparent">
          {title}
        </h1>

        {/* Description */}
        {description && (
          <div className="flex flex-col items-center mt-1.5 md:mt-2">
            <p className="text-white/50 text-sm md:text-base italic text-center max-w-2xl whitespace-pre-line">
              {displayDescription}
            </p>
            {headerImage && (!shouldTruncate || isExpanded) && (
              <Image
                src={headerImage}
                alt=""
                width={750}
                height={350}
                className={`mt-5 sm:mt-6 h-auto w-full rounded-xl ring-1 ring-amber-500/20 shadow-[0_10px_30px_-15px_rgba(245,158,11,0.25)] ${
                  headerImageFullWidth ? "" : "max-w-[750px]"
                }`}
                sizes={
                  headerImageFullWidth
                    ? "(max-width: 896px) 100vw, 896px"
                    : "(max-width: 750px) 100vw, 750px"
                }
              />
            )}
            {shouldTruncate && (
              <Button
                variant="light"
                size="sm"
                className="mt-1 text-amber-500/80 hover:text-amber-400"
                onPress={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? t("common.showLess") : t("common.showMore")}
              </Button>
            )}
          </div>
        )}

        {/* Header image (no description) */}
        {headerImage && !description && (
          <Image
            src={headerImage}
            alt=""
            width={750}
            height={350}
            className={`mt-5 sm:mt-6 h-auto w-full rounded-xl ring-1 ring-amber-500/20 shadow-[0_10px_30px_-15px_rgba(245,158,11,0.25)] ${
              headerImageFullWidth ? "" : "max-w-[750px]"
            }`}
            sizes={
              headerImageFullWidth
                ? "(max-width: 896px) 100vw, 896px"
                : "(max-width: 750px) 100vw, 750px"
            }
          />
        )}

        {/* Actions */}
        {actions && actions.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 sm:flex-shrink-0 justify-center mt-5 sm:mt-6 md:mt-8">
            {actions.map((action, index) => (
              <div key={index}>{action}</div>
            ))}
          </div>
        )}

        {/* Decorative bottom line - hidden on mobile */}
        <div className="hidden sm:block absolute left-1/2 bottom-0 h-px w-[150px] md:w-[200px] -translate-x-1/2 bg-gradient-to-r from-transparent via-amber-500/50 to-transparent" />
      </div>
    </header>
  );
}
